import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Section from "../layout/Section";
import MicroLabel from "../ui/MicroLabel";
import Reveal from "../anim/Reveal";
import { SITE_CONTENT } from "../../constants/siteContent";
import styles from "./Collage.module.css";

/*
 * Collage — the editorial spread (ref s03-s06): seven pieces scattered at
 * varied sizes and offsets on a 12-column grid, a micro caption ABOVE each,
 * generous emptiness, and one serif pull-quote. Paper, ~240vh on desktop.
 *
 * Every word and image comes from src/constants/siteContent.js — edit THERE:
 *   client pieces  caseStudies.items[]      .screenshot + .label (+ .outcome in the alt)
 *   Zeta pieces    ownProduct.promo.gallery the entries whose .file is listed in
 *                                           ZETA_FILES below (their .alt is the alt)
 *   captions       the product LABEL only. SITE_CONTENT stores no year and no
 *                  place per project, so none is shown.
 *   pull-quote     the first QUOTE_SENTENCES sentences of partnerIntro.description,
 *                  verbatim. It is the company's own copy, so it is attributed to
 *                  the brand with the "A" monogram — NOT to a named person, and
 *                  there is no signature (no such asset exists).
 *
 * Layout lives in Collage.module.css: each piece is placed by its slot class
 * (slotA-slotG). 12-column scatter >= 981px, 2-column masonry 561-980px, a
 * single column below. DOM order is the reading order on small screens.
 *
 * Motion
 *   - each figure rides <Reveal> (reduced-motion safe in its own CSS)
 *   - parallax: ONE passive, rAF-throttled scroll listener writes
 *     --collage-drift on each piece's .drift wrapper. The wrapper is a PARENT of
 *     the Reveal element (never the same node), and the distance is measured on
 *     the untransformed grid item, so the effect cannot feed back into itself.
 *     It is off under prefers-reduced-motion, off <= 980px, idle while the track
 *     is off-screen or the tab is hidden, and fully torn down on unmount. The
 *     CSS only reads the variable inside a matching media query, so a stale
 *     value is inert.
 */

/* TODO(content): the section's heading. Visually hidden — it exists so the
   document outline holds; the spread itself carries no visible title. */
const HEADING = "Selected work";
const TITLE_ID = "collage-title";

/* TODO(content): where a piece links to. */
const CLIENT_PATH = "/case-studies";
const ZETA_PATH = "/zeta";

/* TODO(content): which Zeta launch creatives appear, in slot order. A file is
   shown only while ownProduct.promo.gallery still lists it (that entry supplies
   the alt text), so curating the gallery curates this section too. */
const ZETA_DIR = "/images/zeta/";
const ZETA_FILES = ["zeta-delivery.jpg", "zeta-cart.jpg"];

/* TODO(content): intrinsic pixel size of the assets (width / height attributes,
   so nothing shifts while a lazy image loads). Update if the files are re-cut.
   The matching aspect-ratios live in Collage.module.css. */
const CLIENT_SIZE = { width: 1600, height: 1000 };
const ZETA_SIZE = { width: 941, height: 1672 };

/* TODO(content): how many leading sentences of partnerIntro.description make
   the pull-quote. */
const QUOTE_SENTENCES = 2;

/* TODO(content): the composition, in DOM (= small-screen reading) order. `slot`
   names the placement class in Collage.module.css; `kind` says which queue
   fills it (client screenshots are landscape, Zeta creatives are portrait);
   `speed` is the parallax rate — 1 = moves with the page, > 1 travels faster
   through the viewport, < 1 lags behind. Keep it within 0.85-1.15, and keep the
   first and last slots gentle so they never drift out of the section. A slot
   whose queue has run dry is simply left empty. */
const SLOTS = [
  { slot: "a", kind: "client", speed: 1.06 },
  { slot: "b", kind: "client", speed: 0.9 },
  { slot: "c", kind: "zeta", speed: 0.94 },
  { slot: "d", kind: "client", speed: 1.12 },
  { slot: "e", kind: "zeta", speed: 0.88 },
  { slot: "f", kind: "client", speed: 1.15 },
  { slot: "g", kind: "client", speed: 0.92 }
];

/* The pull-quote sits after this many pieces in the DOM. */
const QUOTE_AFTER = 1;

const SLOT_CLASS = {
  a: styles.slotA,
  b: styles.slotB,
  c: styles.slotC,
  d: styles.slotD,
  e: styles.slotE,
  f: styles.slotF,
  g: styles.slotG
};

const REDUCED_QUERY = "(prefers-reduced-motion: reduce)";
const DESKTOP_QUERY = "(min-width: 981px)";
const DRIFT_VAR = "--collage-drift";
const MAX_DRIFT = 96; // px — a hard bound, whatever the viewport height

function cx(...values) {
  return values.filter(Boolean).join(" ");
}

/* Local public/ assets must go through PUBLIC_URL (CRA). */
function asset(path) {
  return `${process.env.PUBLIC_URL}${path}`;
}

/* Names are authored as "Brand | Descriptor". Fallback only — `label` wins. */
function brandOf(name) {
  return String(name || "")
    .split("|")[0]
    .trim();
}

/* The first `count` sentences, verbatim (inner spacing and punctuation kept).
   No regex lookbehind on purpose: it is a syntax error in Safari < 16.4. */
function leadSentences(text, count) {
  const clean = String(text || "").trim();
  const sentences = clean.match(/[^.!?]+[.!?]+(\s+|$)/g);
  return sentences ? sentences.slice(0, count).join("").trim() : clean;
}

const caseItems = (SITE_CONTENT.caseStudies && SITE_CONTENT.caseStudies.items) || [];
const product = SITE_CONTENT.ownProduct || {};
const gallery = (product.promo && product.promo.gallery) || [];

const CLIENT_SHOTS = caseItems
  .filter((item) => item && item.screenshot)
  .map((item) => {
    const label = item.label || brandOf(item.name);
    return {
      label,
      alt: [label, item.outcome].filter(Boolean).join(": "),
      src: asset(item.screenshot),
      to: CLIENT_PATH,
      portrait: false,
      ...CLIENT_SIZE
    };
  });

const ZETA_SHOTS = ZETA_FILES.map((file) => gallery.find((entry) => entry && entry.file === file))
  .filter(Boolean)
  .map((entry) => ({
    label: product.name || "",
    alt: entry.alt || product.name || "",
    src: asset(`${ZETA_DIR}${entry.file}`),
    to: ZETA_PATH,
    portrait: true,
    ...ZETA_SIZE
  }));

function buildPieces() {
  const queues = { client: CLIENT_SHOTS.slice(), zeta: ZETA_SHOTS.slice() };

  return SLOTS.reduce((pieces, { slot, kind, speed }) => {
    const shot = queues[kind] ? queues[kind].shift() : null;
    return shot ? pieces.concat({ ...shot, slot, speed }) : pieces;
  }, []);
}

const PIECES = buildPieces();
const BRAND = String(SITE_CONTENT.brand || "").trim();
const MONOGRAM = BRAND.charAt(0);
const QUOTE = leadSentences(
  SITE_CONTENT.partnerIntro && SITE_CONTENT.partnerIntro.description,
  QUOTE_SENTENCES
);

function watchQuery(query, handler) {
  if (query.addEventListener) {
    query.addEventListener("change", handler);
    return () => query.removeEventListener("change", handler);
  }
  if (query.addListener) {
    query.addListener(handler);
    return () => query.removeListener(handler);
  }
  return () => {};
}

/* Parallax. Each [data-speed] grid item is measured (it never moves); its first
   child — the .drift wrapper — receives the offset:
     drift = (pieceCentre - viewportCentre) * (speed - 1)
   so a piece is exactly in place as it crosses the middle of the viewport, a
   speed above 1 runs ahead of the page and one below 1 trails it. Reads are
   batched before writes, and only a custom property is written, so a frame
   never forces a second layout. */
function useCollageParallax(trackRef) {
  useEffect(() => {
    const track = trackRef.current;
    if (
      !track ||
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function" ||
      typeof window.requestAnimationFrame !== "function"
    ) {
      return undefined;
    }

    const pieces = Array.from(track.querySelectorAll("[data-speed]"))
      .map((anchor) => ({
        anchor,
        drift: anchor.firstElementChild,
        speed: parseFloat(anchor.getAttribute("data-speed"))
      }))
      .filter((piece) => piece.drift && Number.isFinite(piece.speed) && piece.speed !== 1);

    if (!pieces.length) {
      return undefined;
    }

    const reduced = window.matchMedia(REDUCED_QUERY);
    const desktop = window.matchMedia(DESKTOP_QUERY);
    const hasObserver = typeof IntersectionObserver !== "undefined";
    const passive = { passive: true };

    let frame = 0;
    let listening = false;
    let inView = !hasObserver;

    const update = () => {
      frame = 0;
      if (!listening || !inView || document.hidden) {
        return;
      }

      const viewportCentre = window.innerHeight / 2;
      const offsets = pieces.map(({ anchor, speed }) => {
        const rect = anchor.getBoundingClientRect();
        const raw = (rect.top + rect.height / 2 - viewportCentre) * (speed - 1);
        return Math.max(-MAX_DRIFT, Math.min(MAX_DRIFT, raw));
      });

      pieces.forEach(({ drift }, index) => {
        drift.style.setProperty(DRIFT_VAR, `${offsets[index].toFixed(1)}px`);
      });
    };

    const schedule = () => {
      if (frame || !listening || !inView || document.hidden) {
        return;
      }
      frame = window.requestAnimationFrame(update);
    };

    const cancel = () => {
      if (frame) {
        window.cancelAnimationFrame(frame);
        frame = 0;
      }
    };

    const reset = () => {
      pieces.forEach(({ drift }) => drift.style.removeProperty(DRIFT_VAR));
    };

    const sync = () => {
      const shouldRun = desktop.matches && !reduced.matches;

      if (shouldRun && !listening) {
        listening = true;
        window.addEventListener("scroll", schedule, passive);
        window.addEventListener("resize", schedule, passive);
        schedule();
      } else if (!shouldRun && listening) {
        listening = false;
        window.removeEventListener("scroll", schedule, passive);
        window.removeEventListener("resize", schedule, passive);
        cancel();
        reset();
      }
    };

    const onVisibility = () => {
      if (document.hidden) {
        cancel();
      } else {
        schedule();
      }
    };

    let observer = null;
    if (hasObserver) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            inView = entry.isIntersecting;
          });
          if (inView) {
            schedule();
          } else {
            cancel();
          }
        },
        { rootMargin: "25% 0px" }
      );
      observer.observe(track);
    }

    const unwatchReduced = watchQuery(reduced, sync);
    const unwatchDesktop = watchQuery(desktop, sync);
    document.addEventListener("visibilitychange", onVisibility);
    sync();

    return () => {
      unwatchReduced();
      unwatchDesktop();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("scroll", schedule, passive);
      window.removeEventListener("resize", schedule, passive);
      if (observer) {
        observer.disconnect();
      }
      listening = false;
      cancel();
      reset();
    };
  }, [trackRef]);
}

/* One piece: caption above, the image inside a link. A file that fails to load
   removes its piece — the spread never shows a broken frame. */
function CollagePiece({ piece }) {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return null;
  }

  return (
    <div
      className={cx(styles.piece, SLOT_CLASS[piece.slot], piece.portrait && styles.portrait)}
      data-speed={piece.speed}
    >
      <div className={styles.drift}>
        <Reveal as="figure" className={styles.figure}>
          {piece.label ? (
            <MicroLabel as="figcaption" className={styles.caption}>
              {piece.label}
            </MicroLabel>
          ) : null}
          <Link className={styles.frame} to={piece.to}>
            <img
              className={styles.shot}
              src={piece.src}
              alt={piece.alt}
              width={piece.width}
              height={piece.height}
              loading="lazy"
              decoding="async"
              onError={() => setFailed(true)}
            />
          </Link>
        </Reveal>
      </div>
    </div>
  );
}

function Collage() {
  const trackRef = useRef(null);
  useCollageParallax(trackRef);

  if (!PIECES.length && !QUOTE) {
    return null;
  }

  return (
    <Section tone="paper" size="lg" container="wide" id="collage" aria-labelledby={TITLE_ID}>
      <h2 id={TITLE_ID} className={styles.srOnly}>
        {HEADING}
      </h2>

      <div className={styles.track} ref={trackRef}>
        {PIECES.slice(0, QUOTE_AFTER).map((piece) => (
          <CollagePiece key={piece.slot} piece={piece} />
        ))}

        {QUOTE ? (
          <Reveal as="blockquote" className={styles.pull}>
            <p className={`quote ${styles.pullText}`}>{QUOTE}</p>
            {BRAND ? (
              <footer className={styles.pullFoot}>
                {MONOGRAM ? (
                  <span className={styles.monogram} aria-hidden="true">
                    {MONOGRAM}
                  </span>
                ) : null}
                <MicroLabel>{BRAND}</MicroLabel>
              </footer>
            ) : null}
          </Reveal>
        ) : null}

        {PIECES.slice(QUOTE_AFTER).map((piece) => (
          <CollagePiece key={piece.slot} piece={piece} />
        ))}
      </div>
    </Section>
  );
}

export default Collage;
